import { Link, useNavigate, useLocation } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { useAuth, AuthLoadingScreen } from './AuthProvider'
import { createLogger } from '../utils/logger'
import OAuthButtons, { EmailDivider } from './auth/OAuthButtons'
import {
  finalizePendingAccessCode,
  persistPendingAccessCodeId,
  readPendingAccessCodeId,
  validateAccessCode,
} from '../utils/accessCode'
import { supabase } from '../services/supabase'
import { posthog, isPostHogReady } from '../services/posthog'

const logger = createLogger('auth')

export const SIGNUP_CONFIRM_EMAIL_MESSAGE =
  'Check your email to confirm your account, then log in.'

const inputClass =
  'w-full rounded-md border border-neutral-300 px-3 py-2.5 text-sm text-neutral-900 placeholder:text-neutral-400 shadow-sm focus:border-neutral-900 focus:outline-none focus:ring-1 focus:ring-neutral-900 disabled:bg-neutral-50'

const linkClass =
  'font-medium text-neutral-900 underline decoration-neutral-300 underline-offset-2 hover:decoration-neutral-900'

function resolveInitialSignupStep(location) {
  if (readPendingAccessCodeId()) return 'signup'
  const params = new URLSearchParams(location.search)
  if (params.get('request') === '1') return 'request'
  return 'gate'
}

function SignupTermsNotice() {
  return (
    <p className="mt-4 text-sm text-neutral-600">
      By creating an account, you agree to our{' '}
      <Link to="/terms-and-conditions" className={linkClass}>
        Terms &amp; Conditions
      </Link>{' '}
      and{' '}
      <Link to="/privacy-policy" className={linkClass}>
        Privacy Policy
      </Link>
      .
    </p>
  )
}

function SignupPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { signUp, user } = useAuth()
  const loginPath = '/login'
  const signupBasePath = '/signup'

  const goToGate = () => {
    setError('')
    setStep('gate')
    navigate({ pathname: signupBasePath, search: '' }, { replace: true })
  }

  const goToRequest = () => {
    setError('')
    setStep('request')
    navigate({ pathname: signupBasePath, search: '?request=1' }, { replace: true })
  }

  const [step, setStep] = useState(() => resolveInitialSignupStep(location))
  const [accessCode, setAccessCode] = useState('')
  const [codeId, setCodeId] = useState(() => readPendingAccessCodeId() || '')

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')

  const [requestEmail, setRequestEmail] = useState('')
  const [requestName, setRequestName] = useState('')
  const [requestCompany, setRequestCompany] = useState('')
  const [requestMessage, setRequestMessage] = useState('')

  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [loading, setLoading] = useState(false)
  const [validating, setValidating] = useState(false)

  useEffect(() => {
    if (isPostHogReady()) {
      posthog.capture('$pageview', { $current_url: window.location.href, page: 'signup' })
    }
  }, [])

  useEffect(() => {
    if (readPendingAccessCodeId()) return
    const params = new URLSearchParams(location.search)
    if (params.get('request') === '1') {
      setStep('request')
    }
  }, [location.search])

  useEffect(() => {
    if (!user) return
    const from = location.state?.from?.pathname || '/app'
    navigate(from, { replace: true })
  }, [user, location.state, navigate])

  const handleValidateCode = async (e) => {
    e.preventDefault()
    if (validating) return

    setError('')
    setValidating(true)

    try {
      const result = await validateAccessCode(accessCode)
      if (result.valid && result.code_id) {
        setCodeId(result.code_id)
        persistPendingAccessCodeId(result.code_id)
        setStep('signup')
        if (isPostHogReady()) posthog.capture('access_code_attempted', { success: true })
      } else {
        setError(result.error || 'Invalid access code')
        if (isPostHogReady()) posthog.capture('access_code_attempted', { success: false })
      }
    } finally {
      setValidating(false)
    }
  }

  const handleRequestAccess = async (e) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    const { error: insertError } = await supabase.from('access_requests').insert({
      email: requestEmail.trim(),
      name: requestName.trim() || null,
      company: requestCompany.trim() || null,
      message: requestMessage.trim() || null,
    })

    if (insertError) {
      logger.warn('Access request failed', { view: 'signup', action: 'request_access' }, insertError)
      setError(insertError.message)
      setLoading(false)
      return
    }

    setStep('request-sent')
    setLoading(false)
  }

  const handleSignupSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setSuccess('')

    if (!codeId) {
      setError('Please enter a valid access code first.')
      goToGate()
      return
    }

    setLoading(true)
    const { data, error: signUpError } = await signUp(email, password)

    if (signUpError) {
      logger.warn('Signup failed', { view: 'signup', auth_method: 'email' })
      setError(signUpError.message)
      setLoading(false)
      return
    }

    if (data.session) {
      const finalize = await finalizePendingAccessCode()
      if (finalize.error) {
        setError(finalize.error)
        setLoading(false)
        return
      }
    } else {
      navigate(loginPath, {
        replace: true,
        state: { signupSuccess: SIGNUP_CONFIRM_EMAIL_MESSAGE },
      })
    }
  }

  if (user) {
    return <AuthLoadingScreen />
  }

  return (
    <div className="flex min-h-screen flex-col bg-white text-neutral-900">
      <div className="flex flex-1 flex-col items-center justify-center px-6 py-12">
        <Link to="/" className="mb-10 block">
          <img
            src="/ls-logo.svg"
            alt="LandSentry"
            className="mx-auto h-14 w-auto md:h-16"
            width={280}
            height={70}
          />
        </Link>

        <div className="w-full max-w-sm rounded-lg border border-neutral-200 bg-white p-8 shadow-sm">
          {step === 'gate' && (
            <>
              <h1 className="text-lg font-semibold tracking-tight text-neutral-900">Enter your access code</h1>
              <p className="mt-1 text-sm text-neutral-500">LandSentry is currently invite-only.</p>

              {error && (
                <div className="mt-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800" role="alert">
                  {error}
                </div>
              )}

              <form onSubmit={handleValidateCode} className="mt-6 space-y-4">
                <div>
                  <label htmlFor="access-code" className="sr-only">
                    Access code
                  </label>
                  <input
                    id="access-code"
                    name="accessCode"
                    type="text"
                    autoComplete="off"
                    required
                    value={accessCode}
                    onChange={(e) => setAccessCode(e.target.value)}
                    disabled={validating}
                    placeholder="Access code"
                    className={inputClass}
                  />
                </div>
                <button
                  type="submit"
                  disabled={validating}
                  className="flex w-full items-center justify-center rounded-md bg-neutral-900 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-neutral-800 disabled:cursor-not-allowed disabled:opacity-70"
                >
                  {validating ? (
                    <span className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  ) : (
                    'Continue'
                  )}
                </button>
              </form>

              <p className="mt-6 text-center text-sm text-neutral-600">
                Don&apos;t have a code?{' '}
                <button type="button" onClick={goToRequest} className={linkClass}>
                  Request access
                </button>
              </p>

              <p className="mt-4 text-center text-sm text-neutral-600">
                Already have an account?{' '}
                <Link to={loginPath} className={linkClass}>
                  Log in
                </Link>
              </p>
            </>
          )}

          {step === 'request' && (
            <>
              <h1 className="text-lg font-semibold tracking-tight text-neutral-900">Request access</h1>
              <p className="mt-1 text-sm text-neutral-500">Tell us a bit about yourself and we&apos;ll be in touch.</p>

              {error && (
                <div className="mt-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800" role="alert">
                  {error}
                </div>
              )}

              <form onSubmit={handleRequestAccess} className="mt-6 space-y-4">
                <input
                  type="email"
                  required
                  value={requestEmail}
                  onChange={(e) => setRequestEmail(e.target.value)}
                  disabled={loading}
                  placeholder="Email *"
                  className={inputClass}
                />
                <input
                  type="text"
                  value={requestName}
                  onChange={(e) => setRequestName(e.target.value)}
                  disabled={loading}
                  placeholder="Name"
                  className={inputClass}
                />
                <input
                  type="text"
                  value={requestCompany}
                  onChange={(e) => setRequestCompany(e.target.value)}
                  disabled={loading}
                  placeholder="Company"
                  className={inputClass}
                />
                <textarea
                  value={requestMessage}
                  onChange={(e) => setRequestMessage(e.target.value)}
                  disabled={loading}
                  placeholder="Brief message"
                  rows={3}
                  className={`${inputClass} resize-none`}
                />
                <button
                  type="submit"
                  disabled={loading}
                  className="flex w-full items-center justify-center rounded-md bg-neutral-900 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-neutral-800 disabled:cursor-not-allowed disabled:opacity-70"
                >
                  {loading ? (
                    <span className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  ) : (
                    'Submit request'
                  )}
                </button>
              </form>

              <p className="mt-6 text-center text-sm text-neutral-600">
                <button type="button" onClick={goToGate} className={linkClass}>
                  ← Back to access code
                </button>
              </p>
            </>
          )}

          {step === 'request-sent' && (
            <>
              <h1 className="text-lg font-semibold tracking-tight text-neutral-900">Request received</h1>
              <p className="mt-4 text-sm text-neutral-600">Thanks! We&apos;ll reach out shortly.</p>
              <Link to="/" className={`mt-6 inline-block text-sm ${linkClass}`}>
                ← Back to home
              </Link>
            </>
          )}

          {step === 'signup' && (
            <>
              <h1 className="text-lg font-semibold tracking-tight text-neutral-900">Create account</h1>
              <p className="mt-1 text-sm text-neutral-500">Get started with LandSentry.</p>

              <SignupTermsNotice />

              {error && (
                <div className="mt-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800" role="alert">
                  {error}
                </div>
              )}

              {success && (
                <div className="mt-4 rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800" role="status">
                  {success}
                </div>
              )}

              <div className="mt-6">
                <OAuthButtons mode="signup" disabled={loading} onError={setError} />
              </div>

              <EmailDivider mode="signup" />

              <form onSubmit={handleSignupSubmit} className="space-y-4">
                <input
                  id="signup-email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={loading}
                  placeholder="Email"
                  className={inputClass}
                />
                <input
                  id="signup-password"
                  type="password"
                  autoComplete="new-password"
                  required
                  minLength={8}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading}
                  placeholder="Password (min. 8 characters)"
                  className={inputClass}
                />

                <button
                  type="submit"
                  disabled={loading}
                  className="flex w-full items-center justify-center rounded-md bg-neutral-900 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-neutral-800 disabled:cursor-not-allowed disabled:opacity-70"
                >
                  {loading ? (
                    <span className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  ) : (
                    'Create account'
                  )}
                </button>
              </form>

              <p className="mt-6 text-center text-sm text-neutral-600">
                Already have an account?{' '}
                <Link to={loginPath} className={linkClass}>
                  Log in
                </Link>
              </p>
            </>
          )}
        </div>

        {step !== 'request-sent' && (
          <Link to="/" className="mt-10 text-sm text-neutral-500 transition-colors hover:text-neutral-900">
            ← Back to home
          </Link>
        )}
      </div>
    </div>
  )
}

export default SignupPage
